import { useState, useEffect } from 'react';

interface UseAnimatedPlaceholderOptions {
  placeholders: string[];
  typingSpeed?: number;
  pauseDuration?: number;
  deletingSpeed?: number;
}

interface UseAnimatedPlaceholderReturn {
  displayText: string;
  isTyping: boolean;
}

export default function useAnimatedPlaceholder({
  placeholders,
  typingSpeed = 50,
  pauseDuration = 2000,
  deletingSpeed = 30,
}: UseAnimatedPlaceholderOptions): UseAnimatedPlaceholderReturn {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'deleting'>('typing');

  const currentPlaceholder = placeholders[currentIndex];

  useEffect(() => {
    if (!currentPlaceholder) return;

    let timeout: ReturnType<typeof setTimeout>;

    if (phase === 'typing') {
      if (displayText.length < currentPlaceholder.length) {
        setIsTyping(true);
        timeout = setTimeout(() => {
          setDisplayText(currentPlaceholder.slice(0, displayText.length + 1));
        }, typingSpeed);
      } else {
        setIsTyping(false);
        setPhase('pausing');
      }
    } else if (phase === 'pausing') {
      timeout = setTimeout(() => {
        setPhase('deleting');
      }, pauseDuration);
    } else if (phase === 'deleting') {
      if (displayText.length > 0) {
        setIsTyping(true);
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, deletingSpeed);
      } else {
        setIsTyping(false);
        setCurrentIndex((prev) => (prev + 1) % placeholders.length);
        setPhase('typing');
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, phase, currentPlaceholder, currentIndex, placeholders.length, typingSpeed, pauseDuration, deletingSpeed]);

  return { displayText, isTyping };
}
